import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { delay } from "lodash";
import { PageNotFound } from "@/react/components/shared/ErrorPages/PageNotFound";
import {
  addBugsnagBreadcrumb,
  notifyBugsnag,
} from "@/react/helpers/bugsnagHelpers";
import { NotFoundError } from "@circle-react/config/CustomErrors";

export type RenderFunc = (props: ErrorProps) => ReactNode;

export interface ErrorBoundaryProps {
  children?: ReactNode;
  handleNotFoundError?: boolean;
  renderFunc?: RenderFunc;
  shouldAutoReload?: boolean;
}

export interface ErrorBoundaryState {
  componentStack?: string | null;
  error?: Error;
  errorMessage: string;
  errorStack?: string;
  hasError: boolean;
  reloadCount: number;
}

export interface ErrorProps extends Omit<ErrorBoundaryState, "error"> {
  error: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: "", reloadCount: 0 };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (window.DISABLE_JS_ERROR_BOUNDARIES == "true") {
      throw error;
    }

    // https://github.com/facebook/react/issues/11538
    if (error.message.includes("Failed to execute 'removeChild'")) {
      console.error(
        "There was an error likely caused by Google Translate. Reloading page.",
      );
      this.handleReload();
      return;
    }

    const { reloadCount } = this.state;

    this.setState({
      hasError: true,
      error,
      componentStack: errorInfo?.componentStack,
      errorMessage: error?.message,
      errorStack: error.stack,
      reloadCount: reloadCount,
    });

    console.error("ErrorBoundary Error", error, errorInfo);

    const isNotFoundError = error instanceof NotFoundError;
    if (!isNotFoundError) {
      if (window.electronAPI) {
        addBugsnagBreadcrumb("Desktop Error Boundary");
      }
      notifyBugsnag(error);
    }

    const { shouldAutoReload } = this.props;
    shouldAutoReload && reloadCount < 3 && delay(this.handleReload, 3000);
  }

  handleReload = () => {
    this.setState(prevState => ({
      hasError: false,
      errorMessage: "",
      reloadCount: prevState.reloadCount + 1,
    }));
  };

  override render(): ReactNode {
    const { error, hasError } = this.state;

    const {
      renderFunc,
      handleNotFoundError: shouldHandleNotFoundError,
      children,
    } = this.props;

    const commonProps: ErrorProps = {
      ...this.state,
      error: JSON.stringify(error),
    };

    if (hasError) {
      if (shouldHandleNotFoundError && error instanceof NotFoundError) {
        return <PageNotFound {...commonProps} />;
      }

      if (renderFunc) {
        return renderFunc(commonProps);
      }

      return null;
    }

    return children;
  }
}
